import React, { useCallback, useMemo } from 'react';

import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI, EditorAppearance } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { AcceptRejectScreen } from '@atlassian/generative-ai-modal/screens/AcceptReject';

import type { AIPlugin } from '../../../../aiPluginType';
import { discardStream } from '../../../../pm-plugins/pure-pm-in-editor-direct-streaming/transaction-builders/buildDiscardTransaction';
import type { ResponseFeedbackButtonClickAEP } from '../../../../types/analytics';
import type { FeedbackMetadata, PluginFeedbackMetadata } from '../../../../types/types';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useAnalyticsFlow } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { Container } from '../Container';
import type { Action } from '../useUIState';

export const AcceptRejectSessionScreen = ({
	api,
	dispatch,
	appearance,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	appearance: EditorAppearance;
	dispatch: React.Dispatch<Action>;
}): React.JSX.Element => {
	const { isDisplayingChanges, endExperience, editorPluginAIProvider, editorView } =
		useContentComponentContext();
	const { configItem, lastTriggeredFrom } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => ({
			configItem: state.aiExperienceState?.aiDecorationExperiencePluginState?.configItem,
			lastTriggeredFrom:
				state.aiExperienceState?.aiDecorationExperiencePluginState?.lastTriggeredFrom,
		}),
	);
	const product = editorPluginAIProvider.product;
	// preset prompt or base generate / interrogation
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const analyticsFlow = useAnalyticsFlow();

	const aiExperienceMetadata = useMemo(() => {
		return {
			configItemTitle:
				configItem?.agent?.name || convertExperienceName(configItem?.key?.toString() ?? ''),
			...(configItem?.experienceId && fg('thor_support_google_docs_in_description_improver')
				? { experienceId: configItem?.experienceId }
				: {}),
			lastTriggeredFrom,
		};
	}, [configItem, lastTriggeredFrom]);

	const onToggleChanges = useCallback(() => {
		// toggle diff, diff will also be turned off when unmount ExperienceApplication
		const toggleCmd = api?.trackChanges?.commands?.toggleChanges;
		if (toggleCmd) {
			api?.core.actions.execute(toggleCmd);
		}
	}, [api]);

	const onAccept = useCallback(() => {
		api?.analytics?.actions?.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_CHANGES_ACCEPT_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				appearance,
				configItemTitle: configItem?.agent?.name || configItem?.key,
				lastTriggeredFrom,
				product,
			},
		});
		api?.analytics?.actions?.fireAnalyticsEvent(
			createUnifiedAnalyticsPayload({
				action: ACTION.ACTIONED,
				interactionID: analyticsFlow.getLastAiInteractionId(),
				experienceName: convertExperienceName(configItem?.key?.toString() ?? ''),
				isProactive: false,
				attributes: {
					...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
					aiResultAction: 'accepted',
					aiActionedType: fg('platform_editor_ai_aifc_patch_ga') ? 'manuallyAccepted' : undefined,
				},
				invokedFrom: lastTriggeredFrom,
			}),
		);
		endExperience();
		dispatch({ type: 'reset' });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		api,
		appearance,
		configItem,
		lastTriggeredFrom,
		product,
		endExperience,
		analyticsFlow,
		dispatch,
	]);

	const onReject = useCallback(() => {
		api?.analytics?.actions?.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_CHANGES_REJECT_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				appearance,
				configItemTitle: configItem?.agent?.name || configItem?.key,
				lastTriggeredFrom,
				product,
			},
		});
		api?.analytics?.actions?.fireAnalyticsEvent(
			createUnifiedAnalyticsPayload({
				action: ACTION.DISMISSED,
				interactionID: analyticsFlow.getLastAiInteractionId(),
				experienceName: convertExperienceName(configItem?.key?.toString() ?? ''),
				isProactive: false,
				attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
				invokedFrom: lastTriggeredFrom,
			}),
		);

		// api?.core.actions.execute(rejectCommand(editorView?.state));
		// discardStream({ view: editorView, restoreSelection: true });
		discardStream(editorView, true);
		dispatch({ type: 'reset' });
		endExperience();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		api,
		appearance,
		configItem,
		lastTriggeredFrom,
		product,
		analyticsFlow,
		dispatch,
		endExperience,
	]);

	const fireResponseFeedbackEvent = useCallback(
		(
			sentiment: FeedbackMetadata['sentiment'],
			productSuccessState: ResponseFeedbackButtonClickAEP['attributes']['productSuccessStatus'],
		) => {
			fireAIAnalyticsEvent({
				payload: {
					action: 'click',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'responseFeedbackButton',
					attributes: {
						sentiment,
						appearance,
						product,
						configItemTitle: configItem?.agent?.name || configItem?.key,
						lastTriggeredFrom,
						productSuccessStatus: productSuccessState,
						aiInteractionID: analyticsFlow.getLastAiInteractionId(),
					},
					eventType: EVENT_TYPE.UI,
				} as ResponseFeedbackButtonClickAEP,
			});

			fireAIAnalyticsEvent({
				payload: createUnifiedAnalyticsPayload({
					action: ACTION.SUBMITTED,
					interactionID: analyticsFlow.getLastAiInteractionId(),
					experienceName: convertExperienceName(
						configItem?.key?.toString() ?? '',
						lastTriggeredFrom,
					),
					isProactive: false,
					invokedFrom: lastTriggeredFrom,
					attributes: {
						aiFeedbackResult: sentiment === 'good' ? 'up' : 'down',
						...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
					},
				}),
			});
		},
		[configItem, fireAIAnalyticsEvent, analyticsFlow, lastTriggeredFrom, appearance, product],
	);

	const handleFiringFeedbackEvent = useCallback(
		async (feedbackMetadata: FeedbackMetadata) => {
			analyticsFlow.addAttributes({
				userFeedbackSentiment: feedbackMetadata.sentiment,
			});

			// // we need to update the state machine to reflect the user feedback
			// sendToAIExperienceMachine({
			// 	type: 'feedback submitted',
			// 	sentiment: feedbackMetadata.sentiment as 'good' | 'bad',
			// });

			// handleFeedbackSubmission is intended to be implemented
			// by all products -- there will be a period where products won't have
			// it implemented (and will be relying on THUMBS_UP/THUMBS_DOWN)
			// in these cases -- we don't know the product response status
			if (!editorPluginAIProvider.handleFeedbackSubmission) {
				fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'unknown');
				return;
			} else {
				try {
					const productResponse =
						await editorPluginAIProvider.handleFeedbackSubmission(feedbackMetadata);
					fireResponseFeedbackEvent(feedbackMetadata.sentiment, productResponse.status);
				} catch {
					fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'failed');
				}
			}
		},
		[fireResponseFeedbackEvent, editorPluginAIProvider, analyticsFlow],
	);

	const getAIExperience: PluginFeedbackMetadata['getAIExperience'] = useCallback(
		() => ({
			...aiExperienceMetadata,
		}),
		[aiExperienceMetadata],
	);

	const onBadResponseButtonClick = useCallback(() => {
		const feedbackMetadata = {
			sentiment: 'bad' as const,
			getAIExperience,
			editorAttributes: { appearance, product },
		};

		// Now deprecated - migrate to handleFeedbackSubmission
		editorPluginAIProvider.onExperienceEvent &&
			editorPluginAIProvider.onExperienceEvent.THUMBS_DOWN();

		handleFiringFeedbackEvent(feedbackMetadata);
	}, [
		getAIExperience,
		editorPluginAIProvider.onExperienceEvent,
		handleFiringFeedbackEvent,
		appearance,
		product,
	]);

	const onGoodResponseButtonClick = useCallback(() => {
		const feedbackMetadata = {
			sentiment: 'good' as const,
			getAIExperience,
			editorAttributes: { appearance, product },
		};

		// Now deprecated - migrate to handleFeedbackSubmission
		editorPluginAIProvider.onExperienceEvent &&
			editorPluginAIProvider.onExperienceEvent.THUMBS_UP();

		handleFiringFeedbackEvent(feedbackMetadata);
	}, [
		getAIExperience,
		editorPluginAIProvider.onExperienceEvent,
		handleFiringFeedbackEvent,
		appearance,
		product,
	]);

	const acceptRejectScreenRef = React.useRef<HTMLDivElement | null>(null);

	return (
		<Container ref={acceptRejectScreenRef} appearance={appearance}>
			<AcceptRejectScreen
				onAccept={onAccept}
				onReject={onReject}
				onBadResponseButtonClick={onBadResponseButtonClick}
				onGoodResponseButtonClick={onGoodResponseButtonClick}
				isDisplayingChanges={isDisplayingChanges}
				onToggleChanges={onToggleChanges}
			/>
		</Container>
	);
};
